import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import { sortLocalized, type LocalizedText } from '../../../lib/localizedText'
import { InputSwitch } from 'primereact/inputswitch'
import Button from '../../../components/ui/Button'
import Badge from '../../../components/ui/Badge'
import type { Role } from '../types'

interface ColumnOpts {
  t: TFunction
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  busyId: number | null
  onEdit: (row: Role) => void
  onGrants: (row: Role) => void
  onToggle: (row: Role, active: boolean) => void
  onDelete: (row: Role) => void
  /**
   * Salt okunur kip (126, Kısıtlı Yetkili asistan): switch durumu okunur kalsın diye görünür ama pasif;
   * Düzenle/Sil çizilmez. Yetkiler düğmesi KALIR — rolün yetkileri yalnız o pencerede görünür, pencere
   * görüntüleme kipinde açılır.
   */
  readOnly?: boolean
}

/** Rol tablosu sütunları. Durum satır-içi InputSwitch + düzenle/yetkiler/sil. */
export function roleColumns({
  t,
  localized,
  busyId,
  onEdit,
  onGrants,
  onToggle,
  onDelete,
  readOnly = false,
}: ColumnOpts): ReactElement[] {
  return [
    <Column
      key="name"
      header={t('admin.roles.columns.name')}
      // Sıralama GÖRÜNEN değere göre; `field` verilseydi İngilizce arayüzde TR adına göre
      // sıralanır, yani başlık kullanıcıya yanlış söylerdi.
      sortable
      sortFunction={(e) =>
        sortLocalized(e.data as Role[], localized, (r) => [r.nameTr, r.nameEn], e.order)
      }
      body={(row: Role) => localized(row.nameTr, row.nameEn)}
    />,
    <Column
      key="description"
      header={t('admin.roles.columns.description')}
      body={(row: Role) => row.descriptionTr ?? ''}
    />,
    <Column
      key="status"
      header={t('admin.roles.columns.status')}
      style={{ width: '8rem' }}
      body={(row: Role) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active ? t('admin.roles.status.active') : t('admin.roles.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('admin.roles.columns.actions')}
      style={{ width: '13rem' }}
      body={(row: Role) => {
        const active = row.recordStatus === 1
        return (
          <div className="admin-row-actions">
            <InputSwitch
              className="admin-switch"
              checked={active}
              disabled={readOnly || busyId === row.id}
              onChange={(e) => onToggle(row, Boolean(e.value))}
              aria-label={row.nameTr}
              tooltip={t('admin.roles.actions.toggle')}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-key"
              rounded
              text
              aria-label={t('admin.roles.actions.grants')}
              tooltip={t('admin.roles.actions.grants')}
              tooltipOptions={{ position: 'bottom' }}
              onClick={() => onGrants(row)}
            />
            {!readOnly && (
              <>
                <Button
                  icon="pi pi-pencil"
                  rounded
                  text
                  aria-label={t('admin.roles.actions.edit')}
                  tooltip={t('admin.roles.actions.edit')}
                  tooltipOptions={{ position: 'bottom' }}
                  onClick={() => onEdit(row)}
                />
                <Button
                  icon="pi pi-trash"
                  rounded
                  text
                  severity="danger"
                  aria-label={t('admin.roles.actions.delete')}
                  tooltip={t('admin.roles.actions.delete')}
                  tooltipOptions={{ position: 'bottom' }}
                  disabled={busyId === row.id}
                  onClick={() => onDelete(row)}
                />
              </>
            )}
          </div>
        )
      }}
    />,
  ]
}
